import { type Accept } from 'react-dropzone';

import { type ValidationError } from './types';

export function formatBytes(bytes: number, decimals = 1): string {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
    const value = bytes / Math.pow(k, i);

    if (i === 0) {
        return `${value} ${units[i]}`;
    }

    const rounded = Number(value.toFixed(decimals));
    return `${String(rounded)} ${units[i]}`;
}

/**
 * Maps an array of file extensions (e.g. ['.json', '.xlsx']) to the
 * `Accept` shape expected by react-dropzone v14.
 *
 * Extensions are grouped under a wildcard key so that the browser's file
 * picker accepts them via the `accept` attribute. Entries that look like
 * MIME types (e.g. 'image/png' or 'image/*') are passed through as-is.
 */
export function buildAccept(acceptedFileTypes?: string[]): Accept | undefined {
    if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
        return undefined;
    }

    const accept: Accept = {};
    const extensions: string[] = [];

    for (const entry of acceptedFileTypes) {
        const trimmed = entry.trim();
        if (!trimmed) {
            continue;
        }
        if (trimmed.includes('/')) {
            accept[trimmed] = accept[trimmed] ?? [];
            continue;
        }
        const ext = trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
        extensions.push(ext.toLowerCase());
    }

    if (extensions.length > 0) {
        accept['application/octet-stream'] = [
            ...(accept['application/octet-stream'] ?? []),
            ...extensions,
        ];
    }

    return accept;
}

/**
 * Pretty-prints a list of accepted file types (`['.json', '.xlsx']`) for
 * display in the helper text. Returns e.g. ".json, .xlsx".
 */
export function formatAcceptedFileTypes(acceptedFileTypes?: string[]): string {
    if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
        return '';
    }
    return acceptedFileTypes
        .map((ext) => ext.trim())
        .filter(Boolean)
        .map((ext) => (ext.includes('/') || ext.startsWith('.') ? ext : `.${ext}`))
        .join(', ');
}

interface RemainingTimeText {
    key: string;
    values?: Record<string, string | number>;
}

/**
 * Buckets the remaining upload time into a translation key + interpolation
 * values, following the table defined in the Figma design at
 * https://www.figma.com/design/OI1DqEXIqzYdi41E8cM7ea/Optimize-usability?node-id=4454-9629
 * ("Upload remaining time ranges overview").
 *
 * Per-range rounding rules:
 *   0– 5 s    No time shown ("Almost done")
 *   5–30 s    Floor to 5 s; <10 s collapses to "Less than 10 s left";
 *              [25, 30) collapses up to 30 per Anna's 2026-07-14 UX review
 *              (Figma bucket is 25–30 s → "About 30 seconds left").
 *  30–60 s    Floor to 10 s; >=50 s collapses to "Less than a minute left"
 *   1– 5 m    Round to nearest minute (1:30 → 2)
 *   5–10 m    Floor to whole minute
 *  10–20 m    Non-uniform lookup per Anna's 2026-07-02 UX review:
 *              [10:00, 12:00) → 10, [12:00, 14:00) → 12,
 *              [14:00, 16:00) → 15, [16:00, 18:00) → 17,
 *              [18:00, 20:00] → 20.
 *  >20 m      Fallback "This is taking longer than expected"
 *
 * The rounding is intentionally aligned with the update frequencies in the
 * spec (every 5 s / 10 s / 30 s / 60 s / 60–120 s) so the displayed text
 * only changes when the bucket changes, even if the parent updates
 * `remainingMs` at a higher rate.
 *
 * Separate translation keys exist for the second-range buckets because the
 * DE phrasing changes between them ("Etwa N Sekunden übrig" for 5–30 s vs.
 * "Noch etwa N Sekunden" for 30–60 s).
 */
export function formatRemainingTime(remainingMs: number): RemainingTimeText | undefined {
    if (!Number.isFinite(remainingMs) || remainingMs < 0) {
        return undefined;
    }

    const seconds = remainingMs / 1000;

    if (seconds <= 5) {
        return { key: 'UPLOADER.ALMOST_DONE' };
    }

    if (seconds < 30) {
        if (seconds < 10) {
            return { key: 'UPLOADER.LESS_THAN_N_SECONDS', values: { n: 10 } };
        }
        if (seconds >= 25) {
            return { key: 'UPLOADER.ABOUT_N_SECONDS_LEFT', values: { n: 30 } };
        }
        const n = Math.floor(seconds / 5) * 5;
        return { key: 'UPLOADER.ABOUT_N_SECONDS_LEFT', values: { n } };
    }

    if (seconds < 60) {
        if (seconds >= 50) {
            return { key: 'UPLOADER.LESS_THAN_A_MINUTE' };
        }
        const n = Math.floor(seconds / 10) * 10;
        return { key: 'UPLOADER.ABOUT_N_SECONDS', values: { n } };
    }

    const totalMinutes = seconds / 60;

    if (totalMinutes < 5) {
        const n = Math.round(totalMinutes);
        return n === 1
            ? { key: 'UPLOADER.ABOUT_1_MINUTE_LEFT' }
            : { key: 'UPLOADER.ABOUT_N_MINUTES_LEFT', values: { n } };
    }

    if (totalMinutes < 10) {
        const n = Math.floor(totalMinutes);
        return { key: 'UPLOADER.ABOUT_N_MINUTES_LEFT', values: { n } };
    }

    if (totalMinutes <= 20) {
        let n: number;
        if (totalMinutes < 12) {
            n = 10;
        } else if (totalMinutes < 14) {
            n = 12;
        } else if (totalMinutes < 16) {
            n = 15;
        } else if (totalMinutes < 18) {
            n = 17;
        } else {
            n = 20;
        }
        return { key: 'UPLOADER.ABOUT_N_MINUTES_LEFT', values: { n } };
    }

    return { key: 'UPLOADER.TAKING_LONGER' };
}

/**
 * Returns the lowercase extension (with leading dot) of a file name, or an
 * empty string if there is none.
 */
export function getFileExtension(name: string): string {
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

/**
 * Pure validation used to surface friendly error messages on top of what
 * react-dropzone reports. Mirrors the behaviour used by the dropzone but
 * runs locally so the component stays in control of the error text.
 */
export function validateFile(
    file: File,
    options: { acceptedFileTypes?: string[]; maxFileSize?: number },
): ValidationError | undefined {
    const { acceptedFileTypes, maxFileSize } = options;

    if (typeof maxFileSize === 'number' && file.size > maxFileSize) {
        return { code: 'file-too-large', file };
    }

    if (acceptedFileTypes && acceptedFileTypes.length > 0) {
        const ext = getFileExtension(file.name);
        const matchesExt = acceptedFileTypes.some((entry) => {
            const trimmed = entry.trim().toLowerCase();
            if (!trimmed || trimmed.includes('/')) {
                return false;
            }
            const normalized = trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
            return normalized === ext;
        });

        const matchesMime = acceptedFileTypes.some((entry) => {
            const trimmed = entry.trim().toLowerCase();
            if (!trimmed.includes('/')) {
                return false;
            }
            if (trimmed.endsWith('/*')) {
                return file.type.startsWith(trimmed.slice(0, -1));
            }
            return file.type === trimmed;
        });

        if (!matchesExt && !matchesMime) {
            return { code: 'file-invalid-type', file };
        }
    }

    return undefined;
}
